import { escapeHtml } from '../ui.js'
import {
  apiConfigured,
  getTicket,
  replyTicket,
  submitCsat,
  pushSubscribe,
  pushUnsubscribe,
  pushTest,
} from '../api.js'
import {
  currentPushEndpoint,
  disableTicketPush,
  enableTicketPush,
  isStandalone,
} from '../pwa.js'
import {
  renderAttachmentGallery,
  bindLightbox,
  mountComposer,
} from '../attachments.js'

function statusLabel(s) {
  const map = {
    open: 'Abierto',
    waiting: 'Esperando cliente',
    resolved: 'Resuelto',
    closed: 'Cerrado',
  }
  return map[s] || s || '—'
}

export function renderTicket({ token }) {
  const t = token || '—'
  return `
    <div id="ticket-root" data-token="${escapeHtml(t)}">
      <div class="portal-card">
        <p class="portal-muted">Cargando ticket…</p>
        <p class="portal-mono">/t/${escapeHtml(t)}</p>
      </div>
    </div>
  `
}

export function bindTicket(root, { params }) {
  const token = params?.token
  const mount = root.querySelector('#ticket-root')
  if (!mount || !token) return

  if (!apiConfigured()) {
    mount.innerHTML = `<div class="portal-card"><div class="portal-banner warn">Servicio no disponible temporalmente. Contacta por teléfono o email (pie de página).</div><a href="/" data-link class="portal-btn">Inicio</a></div>`
    return
  }

  load()

  async function load() {
    try {
      const data = await getTicket(token)
      await render(data)
    } catch (e) {
      mount.innerHTML = `
        <div class="portal-card">
          <div class="portal-banner warn">${escapeHtml(e.message || 'No encontrado')}</div>
          <a href="/" data-link class="portal-btn">← Centro de soporte</a>
        </div>`
    }
  }

  async function render(data) {
    const t = data.ticket || {}
    const push = data.web_push || {}
    const comments = (t.comments || []).map((c) => `
      <div class="portal-card" style="padding:10px;margin:8px 0;">
        <div style="display:flex;justify-content:space-between;gap:8px;font-size:11px;color:var(--portal-muted);">
          <strong style="color:var(--portal-text);">${escapeHtml(c.from_customer ? (c.author_name || 'Tú') : (c.author_name || 'Soporte'))}</strong>
          <span>${escapeHtml((c.created_at || '').replace('T', ' ').slice(0, 16))}</span>
        </div>
        <p style="margin:8px 0 0;white-space:pre-wrap;font-size:13px;">${escapeHtml(c.body)}</p>
        ${renderAttachmentGallery(c.attachments)}
      </div>
    `).join('')

    const replyBlock = t.can_customer_reply
      ? `<form id="reply-form" class="portal-card">
          <h2 style="font-size:14px;">Tu respuesta</h2>
          <textarea class="portal-textarea" name="body" maxlength="10000"></textarea>
          <button type="submit" class="portal-btn portal-btn-primary" style="margin-top:8px;">Enviar</button>
          <p id="reply-msg" class="portal-muted"></p>
        </form>`
      : `<div class="portal-banner info">Podrás responder cuando el equipo de soporte conteste.</div>`

    const csatBlock = t.show_csat
      ? `<form id="csat-form" class="portal-card">
          <h2 style="font-size:14px;">¿Cómo valoras la atención?</h2>
          <div class="portal-field">
            <select class="portal-select" name="csat_rating" required>
              <option value="">Selecciona…</option>
              <option value="5">5 — Excelente</option>
              <option value="4">4 — Bien</option>
              <option value="3">3 — Regular</option>
              <option value="2">2 — Mal</option>
              <option value="1">1 — Muy mal (comentario obligatorio)</option>
            </select>
          </div>
          <div class="portal-field">
            <label class="portal-label">Comentario</label>
            <textarea class="portal-textarea" name="csat_comment" maxlength="2000"></textarea>
          </div>
          <button type="submit" class="portal-btn portal-btn-primary">Enviar valoración</button>
          <p id="csat-msg" class="portal-muted"></p>
        </form>`
      : (t.csat?.rating
        ? `<div class="portal-banner info">Valoración enviada: ${t.csat.rating}/5</div>`
        : '')

    const endpoint = await currentPushEndpoint()
    const pushReady = !!push.configured && !!push.public_key
    const deviceSubscribed = !!endpoint && Number(push.subscriptions || 0) > 0
    let pushBlock = ''
    if (pushReady) {
      const iosHint = (!isStandalone() && /iphone|ipad|ipod/i.test(navigator.userAgent))
        ? `<p class="portal-muted" style="margin:8px 0 0;font-size:12px;">En iPhone: instala la app («Añadir a inicio») para recibir avisos.</p>`
        : ''
      pushBlock = `
        <div class="portal-card" id="push-card">
          <h2 style="font-size:14px;margin:0 0 6px;">Avisos en el móvil</h2>
          <p class="portal-muted" style="margin:0;font-size:13px;">
            Recibe una notificación cuando soporte responda o cambie el estado del ticket.
          </p>
          ${iosHint}
          <div class="portal-actions" style="margin-top:10px;flex-wrap:wrap;gap:8px;">
            ${deviceSubscribed
              ? `<button type="button" class="portal-btn" id="push-off">Desactivar avisos</button>
                 <button type="button" class="portal-btn portal-btn-primary" id="push-test">Probar</button>`
              : `<button type="button" class="portal-btn portal-btn-primary" id="push-on">Activar avisos</button>`}
          </div>
          <p id="push-msg" class="portal-muted" style="margin:8px 0 0;"></p>
        </div>`
    }

    mount.innerHTML = `
      <div class="portal-card">
        <div style="display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;">
          <div>
            <p class="portal-mono" style="margin:0 0 4px;">${escapeHtml(t.ticket_number || '')}</p>
            <h1 style="font-size:18px;margin:0;">${escapeHtml(t.subject || '')}</h1>
          </div>
          <div style="text-align:right;font-size:12px;color:var(--portal-muted);">
            <div>${escapeHtml(statusLabel(t.status))}</div>
            <div>${escapeHtml(t.organization?.name || '')}</div>
          </div>
        </div>
        <p style="margin:12px 0 0;white-space:pre-wrap;font-size:13px;">${escapeHtml(t.description || '')}</p>
        ${renderAttachmentGallery(t.attachments)}
      </div>
      ${pushBlock}
      <h2 style="font-size:14px;margin:16px 0 8px;">Conversación</h2>
      ${comments || '<p class="portal-muted">Sin mensajes aún.</p>'}
      ${replyBlock}
      ${csatBlock}
      <div class="portal-actions" style="margin-top:12px;">
        <a href="/" data-link class="portal-btn">← Centro de soporte</a>
      </div>
    `

    const pushMsg = () => mount.querySelector('#push-msg')

    mount.querySelector('#push-on')?.addEventListener('click', async () => {
      const el = pushMsg()
      if (el) el.textContent = 'Activando…'
      try {
        await enableTicketPush(token, push.public_key, pushSubscribe)
        if (el) el.textContent = 'Avisos activados.'
        await load()
      } catch (e) {
        if (el) el.textContent = e.message || 'No se pudieron activar'
      }
    })

    mount.querySelector('#push-off')?.addEventListener('click', async () => {
      const el = pushMsg()
      if (el) el.textContent = 'Desactivando…'
      try {
        await disableTicketPush(token, pushUnsubscribe)
        if (el) el.textContent = 'Avisos desactivados.'
        await load()
      } catch (e) {
        if (el) el.textContent = e.message || 'Error'
      }
    })

    mount.querySelector('#push-test')?.addEventListener('click', async () => {
      const el = pushMsg()
      if (el) el.textContent = 'Enviando prueba…'
      try {
        const res = await pushTest(token)
        if (el) el.textContent = res.message || (res.sent ? 'Prueba enviada' : 'Sin suscripción')
      } catch (e) {
        if (el) el.textContent = e.message || 'Error'
      }
    })

    bindLightbox(mount)
    const replyForm = mount.querySelector('#reply-form')
    const composer = replyForm ? mountComposer(replyForm) : null
    replyForm?.addEventListener('submit', async (ev) => {
      ev.preventDefault()
      const el = mount.querySelector('#reply-msg')
      const fd = new FormData(ev.target)
      // Ensure images from composer memory are present
      if (composer) {
        fd.delete('images[]')
        composer.appendToFormData(fd)
      }
      const body = String(fd.get('body') || '').trim()
      const hasImages = [...fd.keys()].some((k) => k.startsWith('images'))
      if (!body && !hasImages) {
        el.textContent = 'Escribe un mensaje o adjunta una imagen.'
        return
      }
      el.textContent = 'Enviando…'
      try {
        await replyTicket(token, fd)
        await load()
      } catch (e) {
        el.textContent = e.message || 'Error'
      }
    })

    mount.querySelector('#csat-form')?.addEventListener('submit', async (ev) => {
      ev.preventDefault()
      const fd = new FormData(ev.target)
      const el = mount.querySelector('#csat-msg')
      el.textContent = 'Enviando…'
      try {
        await submitCsat(
          token,
          Number(fd.get('csat_rating')),
          String(fd.get('csat_comment') || '') || undefined
        )
        await load()
      } catch (e) {
        el.textContent = e.message || 'Error'
      }
    })
  }
}
